import { useState } from "react";
import { ArrowRight, ArrowLeft, CheckCircle2, Shield, Clock, Globe2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const COUNTRIES = [
  "India", "United States", "United Kingdom", "Germany", "France", "UAE",
  "Saudi Arabia", "Canada", "Australia", "Singapore", "Malaysia", "South Africa", "Other",
];

const BUSINESS_TYPES = ["Retailer", "Distributor", "Wholesaler", "E-commerce", "Other"];
const TRADE_DIRECTIONS = ["Buying from India", "Selling to India", "Both"];
const VOLUMES = ["Under $50K", "$50K–$250K", "$250K–$1M", "$1M+", "Not sure yet"];
const CATEGORIES = [
  "Bags & Luggage", "Apparel", "Footwear", "Watches", "Jewellery & Accessories", "Food Items / FMCG",
];
const CONTACT_PREFS = ["Email", "WhatsApp", "Call"];
const STEP_LABELS = ["Company Profile", "Product Interest", "Contact"];

const TRUST = [
  { icon: Shield, label: "Confidential trade inquiries" },
  { icon: Clock, label: "Response within 24–48 hrs" },
  { icon: Globe2, label: "Export-ready documentation" },
];

const initial = {
  company_name: "",
  country: "",
  business_type: "",
  trade_direction: "",
  purchase_volume: "",
  categories: [],
  private_label: false,
  requirements: "",
  contact_name: "",
  email: "",
  phone: "",
  preferred_contact: "Email",
};

export default function InquiryForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleCategory = (c) =>
    setData((d) => ({
      ...d,
      categories: d.categories.includes(c)
        ? d.categories.filter((x) => x !== c)
        : [...d.categories, c],
    }));

  const validStep1 =
    data.company_name && data.country && data.business_type && data.trade_direction && data.purchase_volume;
  const validStep2 = data.categories.length > 0;
  const validStep3 = data.contact_name && data.email;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data: res } = await api.post("/inquiry", data);
      setSuccess(res.reference);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setData(initial);
    setSuccess(null);
    setError("");
  };

  const progress = success ? 100 : Math.round(((step - 1) / 3) * 100 + 8);

  return (
    <section id="inquiry" className="section-shell">
      <div className="section-inner">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-4 lg:sticky lg:top-[5.5rem] lg:self-start">
            <p className="label-cap text-accent mb-4">Trade Inquiry</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary leading-tight">
              Start your sourcing journey
            </h2>
            <p className="mt-5 text-muted leading-relaxed">
              Tell us about your business and product needs. Our trade team will respond within 24–48
              business hours.
            </p>
            <ul className="mt-6 space-y-3">
              {TRUST.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 text-sm text-muted">
                  <Icon size={18} className="text-accent shrink-0" />
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            <div className="bg-surface border border-border rounded-2xl p-6 md:p-10 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="label-cap text-muted">
                  Step <span className="text-accent">{success ? 3 : step}</span> of 3
                </div>
                <div className="label-cap text-muted">
                  {success ? "Submitted" : STEP_LABELS[step - 1]}
                </div>
              </div>

              <div className="h-1.5 w-full rounded-full overflow-hidden bg-surface-alt mb-6">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progress}%`,
                    background: "linear-gradient(90deg, #C9A227, #E8C84A)",
                  }}
                />
              </div>

              {success ? (
                <div className="text-center py-10">
                  <CheckCircle2 size={48} className="mx-auto text-accent-2 mb-4" />
                  <h3 className="font-serif text-2xl font-bold text-primary">Thank you!</h3>
                  <p className="mt-3 text-muted">
                    Reference:{" "}
                    <span className="font-mono text-accent font-semibold">{success}</span>
                  </p>
                  <p className="mt-2 text-sm text-muted">
                    Your inquiry has been emailed to our trade team. We will respond within 24–48
                    business hours.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-8 text-sm text-accent link-underline"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <>
                  {step === 1 && (
                    <div className="space-y-4">
                      <Field label="Company Name *">
                        <input
                          className="input-field"
                          value={data.company_name}
                          onChange={(e) => set("company_name", e.target.value)}
                        />
                      </Field>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Country *">
                          <select
                            className="input-field"
                            value={data.country}
                            onChange={(e) => set("country", e.target.value)}
                          >
                            <option value="">Select country</option>
                            {COUNTRIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Business Type *">
                          <select
                            className="input-field"
                            value={data.business_type}
                            onChange={(e) => set("business_type", e.target.value)}
                          >
                            <option value="">Select type</option>
                            {BUSINESS_TYPES.map((b) => (
                              <option key={b} value={b}>
                                {b}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <Field label="Trade Direction *">
                        <select
                          className="input-field"
                          value={data.trade_direction}
                          onChange={(e) => set("trade_direction", e.target.value)}
                        >
                          <option value="">Select direction</option>
                          {TRADE_DIRECTIONS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Annual Purchase Volume *">
                        <select
                          className="input-field"
                          value={data.purchase_volume}
                          onChange={(e) => set("purchase_volume", e.target.value)}
                        >
                          <option value="">Select volume</option>
                          {VOLUMES.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <Field label="Categories of Interest *">
                        <div className="flex flex-wrap gap-2">
                          {CATEGORIES.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => toggleCategory(c)}
                              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                                data.categories.includes(c)
                                  ? "bg-accent text-primary border-accent"
                                  : "bg-surface border-border text-muted hover:border-accent"
                              }`}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Private Label / Custom Branding">
                        <div className="flex gap-3">
                          {[true, false].map((val) => (
                            <button
                              key={String(val)}
                              type="button"
                              onClick={() => set("private_label", val)}
                              className={`px-5 py-2 rounded-full text-sm font-medium border ${
                                data.private_label === val
                                  ? "bg-accent-2 text-white border-accent-2"
                                  : "bg-surface border-border text-muted"
                              }`}
                            >
                              {val ? "Yes" : "No"}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Additional Requirements">
                        <textarea
                          className="input-field min-h-[120px] resize-y"
                          value={data.requirements}
                          onChange={(e) => set("requirements", e.target.value)}
                          placeholder="MOQ, certifications, packaging, delivery timeline..."
                        />
                      </Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <Field label="Contact Person Name *">
                        <input
                          className="input-field"
                          value={data.contact_name}
                          onChange={(e) => set("contact_name", e.target.value)}
                        />
                      </Field>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Email *">
                          <input
                            type="email"
                            className="input-field"
                            value={data.email}
                            onChange={(e) => set("email", e.target.value)}
                          />
                        </Field>
                        <Field label="Phone">
                          <input
                            type="tel"
                            className="input-field"
                            value={data.phone}
                            onChange={(e) => set("phone", e.target.value)}
                          />
                        </Field>
                      </div>
                      <Field label="Preferred Communication">
                        <div className="flex flex-wrap gap-2">
                          {CONTACT_PREFS.map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => set("preferred_contact", p)}
                              className={`px-4 py-2 rounded-full text-sm font-medium border ${
                                data.preferred_contact === p
                                  ? "bg-accent text-primary border-accent"
                                  : "bg-surface border-border text-muted"
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </Field>
                    </div>
                  )}

                  {error && <p className="mt-4 text-sm text-danger">{error}</p>}

                  <div className="flex justify-between mt-8 pt-6 border-t border-border">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={() => setStep((s) => s - 1)}
                        className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-primary"
                      >
                        <ArrowLeft size={16} /> Back
                      </button>
                    ) : (
                      <span />
                    )}
                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={() => setStep((s) => s + 1)}
                        disabled={!((step === 1 && validStep1) || (step === 2 && validStep2))}
                        className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={submit}
                        disabled={!validStep3 || submitting}
                        className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Submitting…" : "Submit Inquiry"}{" "}
                        <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-cap text-muted mb-2">{label}</div>
      {children}
    </label>
  );
}
